import { useEffect, useMemo, useState } from "react";
import { Play, Music as MusicIcon, Loader2, ChevronDown, ChevronRight, Tag, Maximize2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAudio } from "@/context/AudioContext";
import { useVideoPlayer } from "@/context/VideoPlayerContext";
import AudienceHeartbeat from "@/components/AudienceHeartbeat";

const YT_THUMB = (id) => `https://img.youtube.com/vi/${id}/mqdefault.jpg`;
const UNCATEGORIZED = "Sem categoria";

const MusicPlaylist = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genreFilter, setGenreFilter] = useState("all");
  const [collapsed, setCollapsed] = useState({});
  const { stop: stopRadio } = useAudio();
  const { current, isPlaying, playQueue, playTrackInQueue, expand, isMinimized } = useVideoPlayer();

  useEffect(() => {
    api
      .get("/music/playlist")
      .then((r) => setTracks(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    stopRadio();
  }, [stopRadio]);

  const visibleTracks = useMemo(() => {
    if (genreFilter === "all") return tracks;
    return tracks.filter((t) => (t.genre || UNCATEGORIZED) === genreFilter);
  }, [tracks, genreFilter]);

  const grouped = useMemo(() => {
    const groups = new Map();
    tracks.forEach((t) => {
      const key = t.genre || UNCATEGORIZED;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b, "pt-BR"));
  }, [tracks]);

  const chips = useMemo(() => {
    const list = [{ key: "all", label: "Todas", count: tracks.length }];
    grouped.forEach(([genre, arr]) => list.push({ key: genre, label: genre, count: arr.length }));
    return list;
  }, [grouped, tracks.length]);

  const playFromList = (track) => {
    // If this track is already in the current queue, just switch to it
    if (current?.id === track.id) {
      expand();
      return;
    }
    // Otherwise, build a new queue based on the visible list, starting at this track
    const start = visibleTracks.findIndex((x) => x.id === track.id);
    playQueue(visibleTracks, Math.max(0, start));
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10 flex items-center justify-center py-24 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Carregando playlist…
      </div>
    );
  }

  if (tracks.length === 0) {
    return (
      <section className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
        <div className="mb-2 text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">Fala Music</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-[var(--fi-blue)] leading-[0.9] mb-6">Playlist Fala</h1>
        <div className="rounded-2xl bg-white p-10 text-center text-gray-400 shadow-[0_6px_20px_rgba(13,27,75,0.05)]">
          <MusicIcon className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          Nenhuma música na playlist ainda.
          <div className="text-xs text-gray-400 mt-1">A equipe está preparando novas faixas.</div>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
      <AudienceHeartbeat channel="music" />

      <div className="mb-2 text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">Fala Music</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-[var(--fi-blue)] leading-[0.9] mb-4">Playlist Fala</h1>
      <p className="text-sm text-gray-600 max-w-2xl mb-6">
        Toque em uma faixa e minimize o player <Minimize2InlineHint /> para continuar ouvindo enquanto navega pela Rádio, TV, Cidadão…
      </p>

      {/* Currently-playing pill (only when the mini player is showing) */}
      {current && isMinimized && (
        <button
          onClick={expand}
          data-testid="music-open-mini"
          className="mb-4 w-full sm:w-auto inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-[var(--fi-blue)] text-white shadow-[0_6px_20px_rgba(13,27,75,0.15)] hover:bg-[var(--fi-blue-2)] transition-colors"
        >
          <img src={YT_THUMB(current.youtube_id)} alt="" className="w-10 h-6 object-cover rounded" />
          <div className="flex-1 min-w-0 text-left">
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Tocando · Toque para expandir</div>
            <div className="font-bold text-sm truncate">{current.title}</div>
          </div>
          <Maximize2 className="w-4 h-4 shrink-0" />
        </button>
      )}

      {/* Genre filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-4 px-4 md:mx-0 md:px-0" data-testid="music-genre-filters">
        {chips.map((c) => {
          const active = genreFilter === c.key;
          return (
            <button
              key={c.key}
              data-testid={`music-genre-chip-${c.key}`}
              onClick={() => setGenreFilter(c.key)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${
                active
                  ? "bg-[var(--fi-red)] text-white shadow-[0_4px_12px_rgba(215,38,56,0.35)]"
                  : "bg-white text-[var(--fi-blue)] hover:bg-gray-50 border border-gray-200"
              }`}
            >
              {c.key === "all" ? null : <Tag className="w-3 h-3" />}
              {c.label}
              <span className={`text-[10px] font-normal ${active ? "text-white/70" : "text-gray-500"}`}>· {c.count}</span>
            </button>
          );
        })}
      </div>

      {/* Grouped playlist by genre */}
      <div className="mt-2">
        <div className="text-xs uppercase tracking-widest font-bold text-[var(--fi-red)] mb-3">Categorias</div>
        <div className="space-y-3">
          {grouped
            .filter(([genre]) => genreFilter === "all" || genre === genreFilter)
            .map(([genre, arr]) => {
              const isCollapsed = !!collapsed[genre];
              return (
                <div key={genre} className="rounded-xl bg-white shadow-[0_4px_12px_rgba(13,27,75,0.04)] overflow-hidden" data-testid={`music-group-${genre}`}>
                  <button
                    onClick={() => setCollapsed((c) => ({ ...c, [genre]: !isCollapsed }))}
                    data-testid={`music-group-toggle-${genre}`}
                    className="w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    {isCollapsed ? <ChevronRight className="w-4 h-4 text-[var(--fi-blue)]" /> : <ChevronDown className="w-4 h-4 text-[var(--fi-blue)]" />}
                    <span className="font-display font-black uppercase text-[var(--fi-blue)] tracking-wide">{genre}</span>
                    <span className="text-xs text-gray-500 font-normal">· {arr.length} {arr.length === 1 ? "faixa" : "faixas"}</span>
                  </button>
                  {!isCollapsed && (
                    <div className="px-2 pb-2 space-y-1.5">
                      {arr.map((t) => {
                        const isActive = current?.id === t.id;
                        return (
                          <button
                            key={t.id}
                            data-testid={`music-track-${t.id}`}
                            onClick={() => playFromList(t)}
                            className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-colors text-left ${
                              isActive
                                ? "bg-[var(--fi-blue)] text-white shadow-[0_6px_20px_rgba(13,27,75,0.15)]"
                                : "bg-white text-[var(--fi-blue)] hover:bg-gray-50"
                            }`}
                          >
                            <img src={YT_THUMB(t.youtube_id)} alt="" className="w-14 h-9 object-cover rounded flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-bold truncate text-sm">{t.title}</div>
                              <div className={`text-xs truncate ${isActive ? "text-white/70" : "text-gray-500"}`}>{t.artist}</div>
                            </div>
                            {isActive && isPlaying ? (
                              <div className="flex items-end gap-0.5 h-4">
                                {[0, 1, 2].map((k) => (
                                  <span key={k} className="w-1 bg-white rounded-full sound-bar" style={{ height: "100%", animationDelay: `${k * 0.15}s` }} />
                                ))}
                              </div>
                            ) : (
                              <Play className={`w-4 h-4 ${isActive ? "text-white" : "text-[var(--fi-blue)]/60"}`} />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      <p className="mt-6 text-xs text-gray-400">
        Playlist com vídeos incorporados do YouTube. Anúncios e monetização respeitam os termos do YouTube. O player pode ser minimizado para continuar tocando enquanto você usa outras seções do app.
      </p>
    </section>
  );
};

// Inline hint icon (kept here to avoid another import)
const Minimize2InlineHint = () => (
  <span className="inline-flex align-middle mx-0.5 text-[var(--fi-red)]" aria-hidden="true">
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  </span>
);

export default MusicPlaylist;
